import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { SEO_CONFIG } from '@/config/seo';
import { generateEnhancedKeywords, generateEnhancedTitle, generateEnhancedDescription } from '@/utils/seoKeywordIntegrator';
import { normalizePathForSEO } from '@/utils/urlCleaner';
import { scheduleSeoAudit } from '@/utils/seoCanonicalHreflangAudit';

interface UnifiedSEOManagerProps {
  type: 'homepage' | 'game' | 'server' | 'custom';
  gameName?: string;
  serverData?: {
    id: string;
    name: string;
    description?: string;
    games?: { name: string };
    totalVotes?: number;
    slug?: string;
    logo_image_url?: string;
    banner_image_url?: string;
    total_votes?: number;
    /** Real average rating (0-5). When provided with avgRatingCount > 0, AggregateRating is emitted. */
    avgRating?: number;
    /** Real number of ratings/reviews. */
    avgRatingCount?: number;
  };
  customMeta?: {
    title?: string;
    description?: string;
    keywords?: string;
  };
  /** Override Open Graph image (e.g. game banner). */
  ogImageOverride?: string;
  /** Additional JSON-LD blocks (FAQPage, ItemList, etc.) emitted alongside the auto-generated schema. */
  extraSchemas?: any[];
  /** Canonical override (used by legacy URL aliases pointing to a primary route). */
  canonicalOverride?: string;
  structuredData?: any;
  /** When true, emit robots=noindex (auth-gated / user-action pages that shouldn't be in the SERP). */
  noindex?: boolean;
}

export const UnifiedSEOManager: React.FC<UnifiedSEOManagerProps> = ({
  type,
  gameName,
  serverData,
  customMeta,
  ogImageOverride,
  extraSchemas,
  canonicalOverride,
  structuredData,
  noindex
}) => {
  const { language } = useLanguage();

  const location = useLocation();
  const baseUrl = SEO_CONFIG.site.baseUrl;

  // Retire le canonical de bootstrap (injecté par index.html avant React) pour éviter
  // le doublon avec celui géré par React Helmet ci-dessous.
  useEffect(() => {
    const bootCanonical = document.querySelector('link[rel="canonical"][data-boot-canonical="true"]');
    if (bootCanonical) bootCanonical.parentNode?.removeChild(bootCanonical);
  }, [location.pathname, language]);


  // Tronque proprement une description SEO à 155 caractères sur le dernier espace
  const truncateDescription = (text: string, max = 155): string => {
    if (!text) return '';
    if (text.length <= max) return text;
    const sliced = text.slice(0, max);
    const lastSpace = sliced.lastIndexOf(' ');
    return (lastSpace > 80 ? sliced.slice(0, lastSpace) : sliced).trim() + '…';
  };

  // CORRECTION SEO : Normalisation complète du pathname via urlCleaner
  const cleanPathname = normalizePathForSEO(location.pathname) || '/';
  
  // Construction robuste des URLs canoniques et alternates
  const buildCanonicalUrl = (lang?: string) => {
    const path = cleanPathname.replace(/^\/(en|es)/, '') || '/';
    const isHomepage = path === '/';

    if (!lang || lang === 'fr') {
      return isHomepage ? `${baseUrl}/` : `${baseUrl}${path}`;
    }

    // EN/ES homepage sans trailing slash pour matcher le sitemap et les routes React (/en, /es).
    // Évite le statut GSC "Autre page avec balise canonique correcte" causé par un mismatch /en vs /en/.
    return isHomepage ? `${baseUrl}/${lang}` : `${baseUrl}/${lang}${path}`;
  };

  const buildAlternates = () => {
    const path = cleanPathname.replace(/^\/(en|es)/, '') || '/';
    const isHomepage = path === '/';
    const en = isHomepage ? `${baseUrl}/en` : `${baseUrl}/en${path}`;
    const es = isHomepage ? `${baseUrl}/es` : `${baseUrl}/es${path}`;
    const fr = isHomepage ? `${baseUrl}/` : `${baseUrl}${path}`;

    return [
      { hreflang: 'fr', href: fr },
      { hreflang: 'fr-FR', href: fr },
      { hreflang: 'fr-BE', href: fr },
      { hreflang: 'fr-CA', href: fr },
      { hreflang: 'en', href: en },
      { hreflang: 'en-US', href: en },
      { hreflang: 'en-GB', href: en },
      { hreflang: 'es', href: es },
      { hreflang: 'es-ES', href: es },
      { hreflang: 'es-MX', href: es },
      { hreflang: 'es-AR', href: es },
      // x-default → FR neutre (site FR-first). Pointer vers /en fait consolider Google
      // sur l'EN et provoque "Autre page avec balise canonique correcte" sur les URLs FR.
      { hreflang: 'x-default', href: fr }
    ];
  };


  // Génération dynamique du SEO selon le type
  const generateSEOContent = () => {
    switch (type) {
      case 'homepage':
        return {
          title: SEO_CONFIG.pages.home[language].title,
          description: SEO_CONFIG.pages.home[language].description,
          keywords: SEO_CONFIG.pages.home[language].keywords,
          canonical: buildCanonicalUrl(language),
          alternates: buildAlternates()
        };

      case 'game':
        if (!gameName) return null;
        return {
          title: customMeta?.title || generateEnhancedTitle(gameName, language),
          description: customMeta?.description || generateEnhancedDescription(gameName, language),
          keywords: customMeta?.keywords || generateEnhancedKeywords(gameName, language),
          canonical: buildCanonicalUrl(language),
          alternates: buildAlternates()
        };

      case 'server':
        if (!serverData) return null;
        
        const slug = serverData.slug || serverData.id;
        const gameDisplayName = serverData.games?.name || 'Server';
        const votes = serverData.total_votes || serverData.totalVotes || 0;
        const shortDesc = serverData.description ? 
          (serverData.description.substring(0, 120) + (serverData.description.length > 120 ? '...' : '')) : '';

        // Construction des URLs spécifiques aux serveurs
        const serverAlternates = [
          { hreflang: 'fr-FR', href: `${baseUrl}/serveur/${slug}` },
          { hreflang: 'en-US', href: `${baseUrl}/en/server/${slug}` },
          { hreflang: 'es-ES', href: `${baseUrl}/es/servidor/${slug}` },
          { hreflang: 'x-default', href: `${baseUrl}/serveur/${slug}` }
        ];

        const serverSEO = (() => {
          const votesPart = (label: string) => votes > 0 ? `${votes} ${label} · ` : '';
          switch (language) {
            case 'en':
              return {
                title: `${serverData.name} — ${gameDisplayName} Private Server | Vote`,
                description: `${votesPart('votes')}Join ${serverData.name}, top ${gameDisplayName} private server. ${shortDesc || 'Active community, free to play.'} Vote now!`,
                keywords: `${serverData.name}, ${gameDisplayName} private server, ${gameDisplayName} server list, vote ${gameDisplayName}, best ${gameDisplayName} server`,
                canonical: `${baseUrl}/en/server/${slug}`,
                alternates: serverAlternates
              };
            case 'es':
              return {
                title: `${serverData.name} — Servidor Privado ${gameDisplayName} | Votar`,
                description: `${votesPart('votos')}Únete a ${serverData.name}, servidor privado top de ${gameDisplayName}. ${shortDesc || 'Comunidad activa, gratis.'} ¡Vota ahora!`,
                keywords: `${serverData.name}, servidor privado ${gameDisplayName}, lista servidores ${gameDisplayName}, votar ${gameDisplayName}, mejor servidor ${gameDisplayName}`,
                canonical: `${baseUrl}/es/servidor/${slug}`,
                alternates: serverAlternates
              };
            default: // French
              return {
                title: `${serverData.name} — Serveur Privé ${gameDisplayName} | Vote`,
                description: `${votesPart('votes')}Rejoins ${serverData.name}, top serveur privé ${gameDisplayName}. ${shortDesc || 'Communauté active, gratuit.'} Vote maintenant !`,
                keywords: `${serverData.name}, serveur privé ${gameDisplayName}, top serveur ${gameDisplayName}, vote ${gameDisplayName}, meilleur serveur ${gameDisplayName}`,
                canonical: `${baseUrl}/serveur/${slug}`,
                alternates: serverAlternates
              };
          }
        })();


        return serverSEO;

      case 'custom':
        return {
          title: customMeta?.title || SEO_CONFIG.site.name[language],
          description: customMeta?.description || SEO_CONFIG.site.description[language],
          keywords: customMeta?.keywords || SEO_CONFIG.site.keywords[language],
          canonical: buildCanonicalUrl(language),
          alternates: buildAlternates()
        };

      default:
        return null;
    }
  };

  const seoContent = generateSEOContent();
  if (!seoContent) return null;

  // Image OG optimisée selon le contexte (override prioritaire)
  const getOptimizedOGImage = () => {
    if (ogImageOverride) return ogImageOverride;
    if (type === 'server' && serverData) {
      return serverData.banner_image_url || 
             serverData.logo_image_url || 
             `${baseUrl}/assets/logo.png`;
    }
    return SEO_CONFIG.site.ogImage;
  };

  const ogImage = getOptimizedOGImage();

  // Données structurées avancées selon le type (AggregateRating uniquement avec données réelles)
  const getAdvancedStructuredData = () => {
    if (type === 'server' && serverData) {
      const gameName = serverData.games?.name || 'Server';
      
      const baseData: any = {
        "@context": "https://schema.org",
        "@type": "GameServer",
        "name": serverData.name,
        "description": serverData.description || `Serveur ${gameName} - ${serverData.name}`,
        "url": seoContent.canonical,
        "image": ogImage,
        "game": {
          "@type": "VideoGame",
          "name": gameName
        },
        "provider": {
          "@type": "Organization",
          "name": "RankServeur",
          "url": baseUrl,
          "logo": `${baseUrl}/assets/favicon.png`
        },
        "potentialAction": {
          "@type": "InteractAction",
          "name": language === 'fr' ? "Voter pour ce serveur" : 
                  language === 'es' ? "Votar por este servidor" : "Vote for this server",
          "target": seoContent.canonical
        }
      };

      // AggregateRating uniquement si moyenne réelle fournie (conforme Google Spam Policy 2024)
      // Sinon : pas de rich snippet étoiles, mais aucun risque de pénalité.
      if (
        typeof serverData.avgRating === 'number' &&
        serverData.avgRating > 0 &&
        typeof serverData.avgRatingCount === 'number' &&
        serverData.avgRatingCount > 0
      ) {
        baseData.aggregateRating = {
          "@type": "AggregateRating",
          "ratingValue": serverData.avgRating.toFixed(1),
          "bestRating": "5",
          "worstRating": "1",
          "ratingCount": serverData.avgRatingCount.toString(),
          "reviewCount": serverData.avgRatingCount.toString()
        };
      }

      // BreadcrumbList : améliore l'affichage GSC et aide Google à comprendre la hiérarchie
      // (résout partiellement "Explorée, actuellement non indexée" en enrichissant le contexte).
      const homeLabel = language === 'fr' ? 'Accueil' : language === 'es' ? 'Inicio' : 'Home';
      const gameLabel = language === 'fr' ? `Serveurs ${gameName}` : language === 'es' ? `Servidores ${gameName}` : `${gameName} Servers`;
      const gameSlug = (serverData.games?.name || 'server').toLowerCase().replace(/\s+/g, '-');
      const gamePath = language === 'fr' ? `/jeu/${gameSlug}` : language === 'es' ? `/es/juego/${gameSlug}` : `/en/game/${gameSlug}`;
      const homePath = language === 'fr' ? '/' : language === 'es' ? '/es/' : '/en/';

      const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": homeLabel, "item": `${baseUrl}${homePath}` },
          { "@type": "ListItem", "position": 2, "name": gameLabel, "item": `${baseUrl}${gamePath}` },
          { "@type": "ListItem", "position": 3, "name": serverData.name, "item": seoContent.canonical }
        ]
      };

      return { "@context": "https://schema.org", "@graph": [baseData, breadcrumb] };
    }

    
    if (type === 'game' && gameName) {
      return {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `Top serveurs privés ${gameName}`,
        "description": seoContent.description,
        "url": seoContent.canonical,
        "mainEntity": {
          "@type": "ItemList",
          "name": `Classement serveurs ${gameName}`,
          "description": `Liste des meilleurs serveurs privés ${gameName} classés par votes`
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": language === 'fr' ? "Accueil" : language === 'es' ? "Inicio" : "Home",
              "item": buildCanonicalUrl('fr')
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": `${language === 'fr' ? 'Serveurs' : language === 'es' ? 'Servidores' : 'Servers'} ${gameName}`,
              "item": seoContent.canonical
            }
          ]
        }
      };
    }

    if (type === 'homepage') {
      return {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebSite",
            "@id": `${baseUrl}#website`,
            "name": "RankServeur",
            "alternateName": language === 'fr' ? "Serveurs Privés Gaming" : 
                           language === 'es' ? "Servidores Privados Gaming" : "Private Gaming Servers",
            "description": seoContent.description,
            "url": seoContent.canonical,
            "inLanguage": language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US',
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": `${baseUrl}/?query={search_term}`
              },
              "query-input": "required name=search_term"
            },
            "publisher": {
              "@type": "Organization",
              "name": "RankServeur",
              "url": baseUrl,
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/assets/favicon.png`,
                "width": 60,
                "height": 60
              }
            }
          },
          {
            "@type": "Organization",
            "@id": `${baseUrl}#organization`,
            "name": "RankServeur",
            "url": baseUrl,
            "logo": {
              "@type": "ImageObject",
              "url": `${baseUrl}/assets/favicon.png`,
              "width": 60,
              "height": 60
            },
            "sameAs": [
              "https://twitter.com/rankserveur_official"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "services@rankserveur.com",
              "contactType": "customer service",
              "availableLanguage": ["French", "English", "Spanish"]
            }
          }
        ]
      };
    }
    
    return structuredData;
  };

  const finalStructuredData = getAdvancedStructuredData();
  const finalCanonical = canonicalOverride || seoContent.canonical;
  const finalDescription = truncateDescription(seoContent.description);

  // Audit automatique canonical + hreflang (dev permanent · prod via ?seo-debug=1)
  useEffect(() => {
    scheduleSeoAudit();
  }, [finalCanonical, language, location.pathname]);

  return (
    <Helmet>
      {/* Meta tags essentiels */}
      <title>{seoContent.title}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={seoContent.keywords} />

      {/* Robots : noindex opt-in pour les pages user-action (auth-gated, formulaires),
          index par défaut sur toutes les pages métier. */}
      <meta name="robots" content={noindex
        ? 'noindex,follow'
        : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'} />
      <meta name="googlebot" content={noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large,max-snippet:-1'} />
      <meta name="bingbot" content={noindex ? 'noindex,follow' : 'index,follow'} />


      {/* Canonical */}
      <link rel="canonical" href={finalCanonical} />

      {/* Hreflang - Format standard avec codes ISO */}
      {seoContent.alternates && seoContent.alternates.map(({ hreflang, href }) => (
        <link key={hreflang} rel="alternate" hrefLang={hreflang} href={href} />
      ))}

      {/* Open Graph optimisé */}
      <meta property="og:type" content={type === 'server' ? 'article' : 'website'} />
      <meta property="og:title" content={seoContent.title} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : language === 'en' ? 'en_US' : 'es_ES'} />
      <meta property="og:site_name" content="RankServeur" />
      {/* Content-Language : signal complémentaire pour Bing et anciens crawlers (hreflang reste prioritaire pour Google) */}
      <meta httpEquiv="content-language" content={language === 'fr' ? 'fr-FR' : language === 'en' ? 'en-US' : 'es-ES'} />


      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoContent.title} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@rankserveur_official" />

      {/* Gaming spécifique */}
      <meta name="theme-color" content="#8B5CF6" />
      <meta name="application-name" content="RankServeur" />

      {/* Meta tags gaming spécifiques pour serveurs */}
      {type === 'server' && serverData && (
        <>
          <meta name="game" content={serverData.games?.name || 'Server'} />
          <meta name="server-name" content={serverData.name} />
          <meta name="server-votes" content={(serverData.total_votes || serverData.totalVotes || 0).toString()} />
        </>
      )}

      {/* Données structurées principales */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}

      {/* Schémas additionnels (FAQPage, ItemList, etc.) */}
      {extraSchemas && extraSchemas.filter(Boolean).map((schema, idx) => (
        <script key={`extra-schema-${idx}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};